/**
 * AIVerificationTab - AI Order Verification Alerts
 * Real-time alerts for missing items/upsell opportunities
 */
import { useState, useEffect } from 'react';
import { 
  Brain, AlertTriangle, Check, X, Coffee, 
  ShoppingBag, Plus, RefreshCw, Settings, Eye
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { api } from '@/utils/apiClient';

const ALERT_TYPES = {
  missing_drink: { label: 'Missing Drink', icon: Coffee, color: 'text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30' },
  missing_side: { label: 'Missing Side', icon: ShoppingBag, color: 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/30' },
  few_drinks: { label: 'Few Drinks', icon: AlertTriangle, color: 'text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-950/30' },
  upsell_opportunity: { label: 'Upsell', icon: Plus, color: 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-950/30' }
};

const PRIORITY_COLORS = {
  high: 'border-red-500 bg-red-50 dark:bg-red-950/30',
  medium: 'border-amber-500 bg-amber-50 dark:bg-amber-950/30',
  low: 'border-blue-500 bg-blue-50 dark:bg-blue-950/30'
};

export default function AIVerificationTab() {
  const [rules, setRules] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testItems, setTestItems] = useState([]);
  const [testResult, setTestResult] = useState(null);
  const [showRulesPanel, setShowRulesPanel] = useState(false);

  useEffect(() => {
    fetchRules();
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchRules = async () => {
    try {
      const res = await api.get('/admin/verification-rules');
      setRules(res.data.rules || []);
    } catch (err) {
      console.error('Failed to fetch rules:', err);
    }
    setLoading(false);
  };

  const fetchStats = async () => {
    try {
      const res = await api.get('/admin/verification-stats', { params: { days: 30 } });
      setStats(res.data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  const testVerification = async () => {
    if (testItems.length === 0) {
      toast.error('Add at least one item to test');
      return;
    }
    
    try {
      const res = await api.post('/orders/verify', {
        items: testItems,
        customer_name: 'Test Customer'
      });
      setTestResult(res.data);
      toast.success(`Found ${res.data.alert_count} alert(s)`);
    } catch (err) {
      toast.error('Test failed');
    }
  };

  const addTestItem = (category) => {
    setTestItems([...testItems, { 
      item_id: `test-${Date.now()}`, 
      name: `Test ${category} Item`,
      category,
      quantity: 1 
    }]);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ai-verification-tab">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
            <Brain className="w-6 h-6 text-purple-600 dark:text-purple-400" />
          </div>
          <div>
            <h2 className="text-2xl font-heading text-foreground">AI Order Verification</h2>
            <p className="text-muted-foreground text-sm">Smart alerts for missing items & upsells</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowRulesPanel(!showRulesPanel)}
            className="rounded-full"
          >
            <Settings className="w-4 h-4 mr-2" />
            {showRulesPanel ? 'Hide Rules' : 'View Rules'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => { fetchRules(); fetchStats(); }}
            className="rounded-full"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-card rounded-xl p-4 border border-border">
            <div className="text-2xl font-bold text-foreground">{stats.total_alerts || 0}</div>
            <div className="text-sm text-muted-foreground">Total Alerts (30d)</div>
          </div>
          {Object.entries(stats.stats_by_type || {}).slice(0, 3).map(([type, data]) => (
            <div key={type} className="bg-card rounded-xl p-4 border border-border">
              <div className="text-2xl font-bold text-foreground">{data.total}</div>
              <div className="text-sm text-muted-foreground capitalize">{type.replace('_', ' ')}</div>
              <div className="text-xs text-green-600">{data.accepted} accepted</div>
            </div>
          ))}
        </div>
      )}

      {/* How It Works */}
      <div className="bg-card rounded-2xl border border-border p-6">
        <h3 className="font-heading text-lg text-foreground mb-4">How It Works</h3>
        <div className="grid md:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-muted/30 rounded-xl">
            <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center mx-auto mb-2">
              <Coffee className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div className="font-medium text-foreground text-sm">Breakfast + No Drink?</div>
            <div className="text-xs text-muted-foreground">Suggests adding a coffee or juice</div>
          </div>
          <div className="text-center p-4 bg-muted/30 rounded-xl">
            <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center mx-auto mb-2">
              <ShoppingBag className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="font-medium text-foreground text-sm">Burger + No Side?</div>
            <div className="text-xs text-muted-foreground">Suggests fries or salad</div>
          </div>
          <div className="text-center p-4 bg-muted/30 rounded-xl">
            <div className="w-10 h-10 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center mx-auto mb-2">
              <AlertTriangle className="w-5 h-5 text-orange-600 dark:text-orange-400" />
            </div>
            <div className="font-medium text-foreground text-sm">Large Order?</div>
            <div className="text-xs text-muted-foreground">Checks if enough drinks ordered</div>
          </div>
          <div className="text-center p-4 bg-muted/30 rounded-xl">
            <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto mb-2">
              <Plus className="w-5 h-5 text-green-600 dark:text-green-400" />
            </div>
            <div className="font-medium text-foreground text-sm">Drink Only?</div>
            <div className="text-xs text-muted-foreground">Suggests a pastry upsell</div>
          </div>
        </div>
      </div>

      {/* Test Panel */}
      <div className="bg-card rounded-2xl border border-border p-6">
        <h3 className="font-heading text-lg text-foreground mb-4">Test Verification</h3>
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => addTestItem('breakfast')}>
              + Breakfast
            </Button>
            <Button size="sm" variant="outline" onClick={() => addTestItem('lunch')}>
              + Lunch/Main
            </Button>
            <Button size="sm" variant="outline" onClick={() => addTestItem('coffee')}>
              + Coffee
            </Button>
            <Button size="sm" variant="outline" onClick={() => addTestItem('drinks')}>
              + Drink
            </Button>
            <Button size="sm" variant="outline" onClick={() => addTestItem('sides')}>
              + Side
            </Button>
          </div>
          
          {testItems.length > 0 && (
            <div className="p-4 bg-muted/30 rounded-xl">
              <div className="text-sm font-medium text-foreground mb-2">Test Order:</div>
              <div className="flex flex-wrap gap-2">
                {testItems.map((item, i) => (
                  <span key={i} className="px-3 py-1 bg-card rounded-full text-sm text-foreground border border-border">
                    {item.name}
                    <button 
                      onClick={() => setTestItems(testItems.filter((_, idx) => idx !== i))}
                      className="ml-2 text-muted-foreground hover:text-destructive"
                    >
                      <X className="w-3 h-3 inline" />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}
          
          <div className="flex gap-2">
            <Button onClick={testVerification} disabled={testItems.length === 0}>
              <Eye className="w-4 h-4 mr-2" />
              Run Verification
            </Button>
            <Button variant="outline" onClick={() => { setTestItems([]); setTestResult(null); }}>
              Clear
            </Button>
          </div>
          
          {/* Test Results */}
          {testResult && (
            <div className="mt-4 space-y-3">
              {testResult.alerts.length === 0 ? (
                <div className="p-4 bg-green-50 dark:bg-green-950/30 rounded-xl border border-green-200 dark:border-green-800 text-green-700 dark:text-green-300">
                  <Check className="w-5 h-5 inline mr-2" />
                  Order looks complete - no suggestions
                </div>
              ) : (
                testResult.alerts.map((alert, i) => {
                  const typeConfig = ALERT_TYPES[alert.type] || ALERT_TYPES.upsell_opportunity;
                  const Icon = typeConfig.icon;
                  return (
                    <div 
                      key={i} 
                      className={`p-4 rounded-xl border-l-4 ${PRIORITY_COLORS[alert.priority]}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg ${typeConfig.color}`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="flex-1">
                          <div className="font-medium text-foreground">{alert.message}</div>
                          <div className="text-sm text-muted-foreground mt-1">
                            Priority: {alert.priority} | Category: {alert.category}
                          </div>
                          {alert.suggestions?.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-2">
                              {alert.suggestions.map((s, j) => (
                                <span key={j} className="px-2 py-1 bg-card rounded text-xs text-foreground border border-border">
                                  {s.name} - ${s.price?.toFixed(2)}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      </div>

      {/* Rules Panel */}
      {showRulesPanel && (
        <div className="bg-card rounded-2xl border border-border p-6">
          <h3 className="font-heading text-lg text-foreground mb-4">Verification Rules</h3>
          <div className="space-y-3">
            {rules.map((rule, i) => (
              <div key={i} className="p-4 bg-muted/30 rounded-xl">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-foreground">{rule.name}</div>
                    <div className="text-sm text-muted-foreground">{rule.description}</div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {rule.trigger_categories && `Triggers: ${rule.trigger_categories.join(', ')}`}
                      {rule.min_items && ` | Min items: ${rule.min_items}`}
                      {` | Priority: ${rule.priority}`}
                    </div>
                  </div>
                  <div className={`px-3 py-1 rounded-full text-xs ${
                    rule.priority === 'high' ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300' :
                    rule.priority === 'medium' ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300' :
                    'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                  }`}>
                    {rule.priority}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
